import React, { useEffect } from "react";
import { Box, FormHelperText, Typography } from "@mui/material";
import { FormGroup } from "./FormComponents";
import ComposerStepCard from "./ComposerStepCard";
import PublishScheduleOptions from "../scheduling/PublishScheduleOptions";
import EventRecurrenceSettingCard from "./EventRecurrenceSettingCard";
import EventReminderSettingCard from "./EventReminderSettingCard";
import type { StepProps } from "./StepComponents";
import { getEventStartDateTimeMs } from "../../utils/eventDateValidation";

const EventSettingsStep: React.FC<StepProps> = ({
  formData,
  errors,
  handleChange,
}) => {
  const hasRecurrence =
    Boolean(formData.eventRecurrenceFrequency) &&
    formData.eventRecurrenceFrequency !== "none";
  const isStaffOnly = formData.staffOnly === true;

  useEffect(() => {
    if (!hasRecurrence) return;
    if (formData.publishMode === "schedule") {
      handleChange({ target: { name: "publishMode", value: "now" } });
      handleChange({ target: { name: "publishAt", value: "" } });
    }
  }, [formData.publishMode, handleChange, hasRecurrence]);

  const maxPublishDateTime = (() => {
    const startMs = getEventStartDateTimeMs(
      formData.eventStartDate,
      formData.eventStartTime,
    );
    return startMs !== null ? new Date(startMs) : null;
  })();

  return (
    <FormGroup style={{ marginBottom: 0 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 400,
          color: "primary.main",
          fontSize: { xs: "1.125rem", sm: "1.25rem" },
          mb: { xs: 1, sm: 0.75 },
        }}
      >
        Event Settings
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          mt: { xs: 1, sm: 1.25 },
        }}
      >
        {!isStaffOnly && (
          <EventReminderSettingCard
            formData={formData}
            errors={errors}
            handleChange={handleChange}
          />
        )}

        <EventRecurrenceSettingCard
          formData={formData}
          errors={errors}
          handleChange={handleChange}
        />

        {!hasRecurrence && !isStaffOnly && (
          <>
            <ComposerStepCard title="When should this go live?">
              <PublishScheduleOptions
                publishMode={formData.publishMode || "now"}
                publishAt={formData.publishAt || ""}
                maxDateTime={maxPublishDateTime}
                onPublishModeChange={(mode) =>
                  handleChange({ target: { name: "publishMode", value: mode } })
                }
                onPublishAtChange={(value) =>
                  handleChange({ target: { name: "publishAt", value } })
                }
                hideHeading
              />
            </ComposerStepCard>
            {errors.publishAt && (
              <FormHelperText error sx={{ mt: -0.5 }}>
                {errors.publishAt}
              </FormHelperText>
            )}
          </>
        )}
      </Box>
    </FormGroup>
  );
};

export default EventSettingsStep;
